import type { Conditions } from '../types';

const ALWAYS_FALSE: Conditions = { when: () => false };

// We store condition lists as arrays, but we want deterministic and minimal
// outputs: `undefined` means "no constraint" and duplicates add no value.
const unique = (list?: string[]): string[] | undefined => {
    if (!list?.length) return undefined;
    return Array.from(new Set(list));
};

const union = (a?: string[], b?: string[]): string[] | undefined => {
    if (!a?.length) return unique(b);
    if (!b?.length) return unique(a);
    return unique([...a, ...b]);
};

const intersect = (a?: string[], b?: string[]): { list?: string[]; impossible?: boolean } => {
    // Intersections *narrow* applicability.
    // `undefined`/empty is treated as "unconstrained", so intersecting with it yields the other.
    if (!a?.length) return { list: unique(b) };
    if (!b?.length) return { list: unique(a) };

    const bSet = new Set(b);
    const list = unique(a.filter((x) => bSet.has(x)));

    // An empty intersection means the merged conditions can never apply.
    if (!list?.length) return { impossible: true };
    return { list };
};

/**
 * Merges wrapper `Conditions` (from groups/collections) into leaf-rule conditions.
 *
 * Semantics:
 * - `applicable*` and `includeOfferTypes` are intersected (narrowed).
 * - `exclude*` lists are unioned.
 * - `when` predicates are AND-composed.
 * - If an intersected list becomes empty, returns `{ when: () => false }` to short-circuit.
 *
 * TODO: Consider short-circuiting during config tree walk instead of merging wrapper conditions
 * into each leaf rule. If a group/collection condition fails, stop walking its child paths.
 *
 * Pros:
 * - Potentially fewer rules registered/evaluated (less work, less memory).
 * - Keeps conditions attached to the nodes that own them.
 *
 * Cons:
 * - Requires the walker to have enough runtime context (values/state/entity/offerType) early.
 * - More complex caching/invalidation if those inputs change (may need re-walk/re-register).
 */
export function mergeConditions(parent?: Conditions, child?: Conditions): Conditions | undefined {
    if (!parent) return child;
    if (!child) return parent;

    const entities = intersect(parent.applicableEntities, child.applicableEntities);
    if (entities.impossible) return ALWAYS_FALSE;
    const states = intersect(parent.applicableStates, child.applicableStates);
    if (states.impossible) return ALWAYS_FALSE;
    const offerTypes = intersect(parent.includeOfferTypes, child.includeOfferTypes);
    if (offerTypes.impossible) return ALWAYS_FALSE;

    const parentWhen = parent.when;
    const childWhen = child.when;
    const when =
        parentWhen || childWhen
            ? (ctx: any, value?: unknown, scope?: unknown) =>
                  // Both predicates must pass (logical AND). If either predicate is missing,
                  // treat it as "always true".
                  (typeof parentWhen !== 'function' ? true : Boolean(parentWhen(ctx, value, scope))) &&
                  (typeof childWhen !== 'function' ? true : Boolean(childWhen(ctx, value, scope)))
            : undefined;

    const merged: Conditions = {
        applicableEntities: entities.list,
        applicableStates: states.list,
        includeOfferTypes: offerTypes.list,
        excludeStates: union(parent.excludeStates, child.excludeStates),
        excludeOfferTypes: union(parent.excludeOfferTypes, child.excludeOfferTypes),
        excludeFuelTypes: union(parent.excludeFuelTypes, child.excludeFuelTypes),
        when
    };

    // Strip undefined keys to keep the object minimal
    for (const key of Object.keys(merged) as Array<keyof Conditions>) {
        if (merged[key] === undefined) delete (merged as any)[key];
    }

    return Object.keys(merged).length ? merged : undefined;
}
